import { useToast, UseToastOptions } from "@chakra-ui/react";
import { AxiosError } from "axios";

interface ToastConfig extends UseToastOptions {
  duration: number;
  isClosable: boolean;
  status: "success" | "error" | "info" | "warning";
}

const TOAST_CONFIG: Record<string, ToastConfig> = {
  success: {
    duration: 5000,
    isClosable: true,
    status: "success",
  },
  error: {
    duration: 7000,
    isClosable: true,
    status: "error",
  },
  info: {
    duration: 3000,
    isClosable: true,
    status: "info",
  },
  warning: {
    duration: 5000,
    isClosable: true,
    status: "warning",
  },
};

interface UseCommonToastReturn {
  toast: ReturnType<typeof useToast>;
  showSuccess: (title: string, description: string) => void;
  showError: (title: string, description: string) => void;
  showInfo: (title: string, description: string) => void;
  showWarning: (title: string, description: string) => void;
  showItemCreated: () => void;
  showItemUpdated: () => void;
  showItemSoldOut: (isSoldOut: boolean) => void;
  showMissingFields: (missingFields: string[]) => void;
  showImageUploadError: () => void;
  showImageReplaceError: () => void;
  showApiError: (error: AxiosError | any, defaultMessage?: string) => void;
  showOtpResent: () => void;
  showOtpVerified: () => void;
  showOtpError: () => void;
  showTooManyRequests: (maxRequests: number) => void;
}

export const useCommonToast = (): UseCommonToastReturn => {
  const toast = useToast();

  const showSuccess = (title: string, description: string): void => {
    toast({
      title,
      description,
      ...TOAST_CONFIG.success,
    });
  };

  const showError = (title: string, description: string): void => {
    toast({
      title,
      description,
      ...TOAST_CONFIG.error,
    });
  };

  const showInfo = (title: string, description: string): void => {
    toast({
      title,
      description,
      ...TOAST_CONFIG.info,
    });
  };

  const showWarning = (title: string, description: string): void => {
    toast({
      title,
      description,
      ...TOAST_CONFIG.warning,
    });
  };

  const showItemCreated = (): void => {
    showSuccess("Item created.", "The item was successfully created.");
  };

  const showItemUpdated = (): void => {
    showSuccess("Item updated.", "The item was successfully updated.");
  };

  const showItemSoldOut = (isSoldOut: boolean): void => {
    showSuccess(
      "Item updated.",
      `Item ${isSoldOut ? 'marked as sold out' : 'marked as in stock'}`
    );
  };

  const showMissingFields = (missingFields: string[]): void => {
    const formattedMissingFields = missingFields
      .map((field) => `• ${field}`)
      .join("\n");

    showError(
      "Missing Required Fields",
      `Please fill out the following required fields:\n${formattedMissingFields}`
    );
  };

  const showImageUploadError = (): void => {
    showError("Image Upload Failed.", "Unable to upload the image.");
  };

  const showImageReplaceError = (): void => {
    showError("Image Replace Failed.", "Unable to replace the image.");
  };

  const showApiError = (error: AxiosError | any, defaultMessage: string = "An error occurred."): void => {
    let errorMessage = error.response?.data?.message ||
                      error.response?.data ||
                      error.message ||
                      defaultMessage;

    if (typeof errorMessage === 'string' && errorMessage.includes("value too long for type")) {
      errorMessage = "The image file name is too long.";
    }

    showError(defaultMessage, errorMessage);
  };

  const showOtpResent = (): void => {
    showInfo("OTP Resent", "A new OTP has been sent to your phone number.");
  };

  const showOtpVerified = (): void => {
    showSuccess("Verification Successful", "Your phone number and OTP have been verified.");
  };

  const showOtpError = (): void => {
    showError("Wrong OTP", "The OTP you entered is incorrect. Please try again.");
  };

  const showTooManyRequests = (maxRequests: number): void => {
    showError(
      "Too Many Requests",
      `You have reached the maximum of ${maxRequests} requests. Please wait 5 minutes.`
    );
  };

  return {
    toast,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showItemCreated,
    showItemUpdated,
    showItemSoldOut,
    showMissingFields,
    showImageUploadError,
    showImageReplaceError,
    showApiError,
    showOtpResent,
    showOtpVerified,
    showOtpError,
    showTooManyRequests,
  };
};

export default useCommonToast;
